import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelDeposit,
  createDeposit,
  fetchDepositDetail,
  fetchDepositsList,
  type CreateDepositResponse,
} from '../api/deposits';
import type {
  CreateDepositRequest,
  DepositDetail,
  DepositSummary,
  ListDepositsQuery,
} from '@deposits/shared';

export function useDepositsList(params: ListDepositsQuery | null) {
  return useQuery<DepositSummary[]>({
    queryKey: ['deposits', params],
    queryFn: () => fetchDepositsList(params!),
    enabled: params != null,
    staleTime: 15_000,
  });
}

export function useDepositDetail(id: number | null) {
  return useQuery<DepositDetail>({
    queryKey: ['deposit-detail', id],
    queryFn: () => fetchDepositDetail(id!),
    enabled: id != null,
    staleTime: 15_000,
  });
}

export function useCancelDeposit() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: number; reason?: string | null }>({
    mutationFn: ({ id, reason }) => cancelDeposit(id, reason),
    onSuccess: () => {
      // Tab B should reflect the new "מבוטל" status; Tab A regains the released
      // per-check pool. Invalidate both.
      qc.invalidateQueries({ queryKey: ['deposits'] });
      qc.invalidateQueries({ queryKey: ['receipts-pending'] });
    },
  });
}

export function useCreateDeposit() {
  const qc = useQueryClient();
  return useMutation<CreateDepositResponse, Error, CreateDepositRequest>({
    mutationFn: createDeposit,
    onSuccess: () => {
      // The new deposit should appear in Tab B; the receipts that were just
      // deposited should disappear from Tab A. Invalidate both.
      qc.invalidateQueries({ queryKey: ['deposits'] });
      qc.invalidateQueries({ queryKey: ['receipts-pending'] });
    },
    onError: () => {
      // Likely a race / stale-selection: another deposit grabbed the same
      // receipts first. Refresh the receipts pool so the user's selection
      // reflects current state on retry.
      qc.invalidateQueries({ queryKey: ['receipts-pending'] });
    },
  });
}
